import { useEffect, useState, type ReactNode } from "react";
import {
  clientBuildStamp,
  compareBuildStamp,
  describeBuildStampVerdict,
  type BuildStampVerdict
} from "./buildStamp";
import { AuthFrame } from "../routes/auth/AuthFrame";

/**
 * Refuses to start an application that was built for a different environment than the one serving
 * it.
 *
 * This is the readout the client half never had. `/api/health` reports the Worker's environment and
 * is green whenever the Worker is correct, which it was on 2026-09-02 while the client bundle it
 * served pointed at `http://127.0.0.1:54321`. Nothing anywhere compared the two, so a broken
 * deployment looked healthy from every angle anybody was looking from.
 *
 * It blocks rather than warns. A bundle pointed at the wrong Supabase project cannot sign anybody
 * in, cannot read anything and cannot write anything, so letting it render is offering a portal that
 * only fails later and less legibly.
 *
 * A health endpoint that cannot be reached is NOT a mismatch and does not block. That case is a
 * network problem or a cold start, and refusing to render the application because one request failed
 * would turn a blip into an outage.
 */
export function BuildStampGuard({ children }: { children: ReactNode }) {
  const [verdict, setVerdict] = useState<BuildStampVerdict>({ kind: "unknown" });

  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        const response = await fetch("/api/health", { headers: { accept: "application/json" } });
        const body = (await response.json()) as { environment?: unknown };

        if (!cancelled) {
          setVerdict(compareBuildStamp(clientBuildStamp, body.environment));
        }
      } catch {
        // Unreachable is unknown, and unknown renders the application. See the note above.
        if (!cancelled) {
          setVerdict({ kind: "unknown" });
        }
      }
    }

    void check();

    return () => {
      cancelled = true;
    };
  }, []);

  const message = describeBuildStampVerdict(verdict);

  if (!message) {
    return <>{children}</>;
  }

  // In the frame of the other pages outside the shell since 2026-09-24. The client sketch did not
  // draw this page, so only its look changed: the words are an operator's, because the fault is.
  return (
    <AuthFrame titleId="build-stamp-title" role="alert">
      <p className="k-auth-note">Felaktig deploy</p>
      <h2 id="build-stamp-title" className="k-auth-title">
        Portalen startade inte
      </h2>
      <p>{message}</p>
      <p className="k-stamp">
        <code data-testid="build-stamp">{clientBuildStamp.raw || "(ingen stämpel)"}</code>
      </p>
    </AuthFrame>
  );
}
